import { useAuth } from "@/lib/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, KeyRound, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "@/components/ui/sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

export default function Paywall({ children }) {
  const { user, loading, isPaid, activateGodmode } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [buyingTier, setBuyingTier] = useState(null);

  // ═══ OPEN ACCESS WINDOW ═══
  // Free, no-signin full access until end of first week of June 2026 (June 7 23:59 UTC).
  // After this date, normal auth + paywall gating resumes.
  const OPEN_ACCESS_UNTIL = new Date("2026-06-08T00:00:00Z");
  if (Date.now() < OPEN_ACCESS_UNTIL.getTime()) {
    return children;
  }

  if (loading) return null;

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 text-center space-y-4" data-testid="paywall-login">
        <Lock className="w-8 h-8 text-[#FF0000]/30" />
        <div className="text-sm font-mono text-[#FF0000]/60 tracking-wider">SIGN IN TO ACCESS</div>
        <Button onClick={() => navigate("/auth")} className="bg-[#FF0000] text-black hover:bg-[#FF0000]/80 font-mono" data-testid="paywall-signin-btn">
          SIGN IN / CREATE ACCOUNT
        </Button>
      </div>
    );
  }

  if (isPaid()) return children;

  const subscribe = async (tierId) => {
    setBuyingTier(tierId);
    try {
      const res = await axios.post(`${API}/payments/v1/checkout/session`, {
        package_id: tierId,
        origin_url: window.location.origin,
        return_path: window.location.pathname,
        mirrors_id: "",
        user_id: user.id || "",
      }, { withCredentials: true });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error("Could not start checkout");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    }
    setBuyingTier(null);
  };

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4 text-center space-y-5" data-testid="paywall-upgrade">
      <Lock className="w-8 h-8 text-[#FF0000]/30" />
      <div className="text-sm font-mono text-[#FF0000]/70 tracking-wider">PREMIUM ACCESS REQUIRED</div>
      <div className="text-[10px] font-mono text-[#FF0000]/35 max-w-xs">
        Subscribe to unlock full access to all interactive features, downloads, and tools.
      </div>

      <div className="flex gap-3">
        <button onClick={() => subscribe("tier_13")} disabled={!!buyingTier}
          className="border px-6 py-4 text-center transition-all hover:shadow-[0_0_12px_rgba(255,0,0,0.15)]"
          style={{ borderColor: "rgba(255,0,0,0.3)", background: "rgba(255,0,0,0.03)" }}
          data-testid="paywall-tier-13">
          {buyingTier === "tier_13" ? <Loader2 className="w-4 h-4 mx-auto animate-spin text-[#FF0000]" /> : (
            <>
              <div className="text-lg font-mono font-bold text-[#FF0000]">$13</div>
              <div className="text-[9px] font-mono text-[#FF0000]/40">/month</div>
              <div className="text-[8px] font-mono text-[#FF0000]/25 mt-1">MIRRORS</div>
            </>
          )}
        </button>
        <button onClick={() => subscribe("tier_33")} disabled={!!buyingTier}
          className="border px-6 py-4 text-center transition-all hover:shadow-[0_0_12px_rgba(255,0,0,0.2)]"
          style={{ borderColor: "rgba(255,0,0,0.5)", background: "rgba(255,0,0,0.06)" }}
          data-testid="paywall-tier-33">
          {buyingTier === "tier_33" ? <Loader2 className="w-4 h-4 mx-auto animate-spin text-[#FF0000]" /> : (
            <>
              <div className="text-lg font-mono font-bold text-[#FF0000]">$33</div>
              <div className="text-[9px] font-mono text-[#FF0000]/40">/lifetime</div>
              <div className="text-[8px] font-mono text-[#FF0000]/25 mt-1">GODMODE</div>
            </>
          )}
        </button>
      </div>

      {/* GODMODE code */}
      <div className="w-full max-w-xs space-y-2 border-t border-[#FF0000]/10 pt-4 mt-2">
        <div className="flex items-center justify-center gap-1.5 text-[9px] font-mono text-[#39FF14]/30 tracking-wider">
          <KeyRound className="w-3 h-3" /> ACCESS CODE
        </div>
        <div className="flex gap-1.5">
          <Input value={code} onChange={e => setCode(e.target.value)} placeholder="Enter code..."
            className="bg-black border-[#39FF14]/20 text-[#39FF14] placeholder:text-[#39FF14]/20 font-mono text-xs"
            onKeyDown={e => e.key === "Enter" && code.trim() && handleCode()}
            data-testid="paywall-code-input" />
          <Button onClick={handleCode} disabled={submitting || !code.trim()}
            className="bg-[#39FF14]/15 text-[#39FF14] hover:bg-[#39FF14]/25 border border-[#39FF14]/30 font-mono text-xs"
            data-testid="paywall-code-submit">GO</Button>
        </div>
      </div>
    </div>
  );

  async function handleCode() {
    setSubmitting(true);
    const res = await activateGodmode(code);
    if (res.ok) {
      const msg = res.tier === "lifetime"
        ? "LIFETIME ACCESS UNLOCKED"
        : "Access unlocked for 24 hours";
      toast.success(msg);
    } else toast.error(res.error || "Invalid code");
    setCode("");
    setSubmitting(false);
  }
}
